import { useState } from 'react';
import { DfeInventoryEntry } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DeleteItemDialogProps {
  item: DfeInventoryEntry | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (quantity?: number) => Promise<void>;
}

export function DeleteItemDialog({ item, open, onClose, onConfirm }: DeleteItemDialogProps) {
  const [quantity, setQuantity] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setQuantity('1');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (item?.fungibility === 'fungible') {
      const qty = parseInt(quantity, 10);
      if (!Number.isInteger(qty) || qty < 1) {
        setError('Quantity must be a positive integer');
        return;
      }
      if (item.quantity != null && qty > item.quantity) {
        setError(`Quantity cannot exceed ${item.quantity}`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const qty = item?.fungibility === 'fungible' ? parseInt(quantity, 10) : undefined;
      await onConfirm(qty);
      resetForm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Burn failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Burn {item?.name}?</DialogTitle>
          <DialogDescription>
            This action is irreversible. The item will be permanently destroyed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {item?.fungibility === 'fungible' && (
            <div className="space-y-2">
              <Label htmlFor="burnQuantity">Quantity to burn (max: {item.quantity})</Label>
              <Input
                id="burnQuantity"
                type="number"
                min="1"
                max={item.quantity ?? undefined}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Burning...' : 'Burn'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
